import React, { useEffect, useState } from 'react';
import { localToday, dateStr , classStartsAt} from '../utils/dates';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, addDoc, collection, query, where, getDocs, increment} from 'firebase/firestore';
import Layout from '../components/Layout';
import { logError } from '../utils/errorLogger';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Box, CircularProgress } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PrivateSessionRequestDialog from '../components/PrivateSessionRequestDialog';

const gold = '#C9A84C';
const dark = '#0D1E35';

function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [processing, setProcessing] = useState(true);
  const [done, setDone] = useState(false);
  const [promptReserve, setPromptReserve] = useState(false);
  const [reserveClassType, setReserveClassType] = useState('');
  const [paAthleteId, setPaAthleteId] = useState('');
  const [reservingId, setReservingId] = useState(null);
  const [reservedIds, setReservedIds] = useState([]); // multiple, up to session count
  const [reserveMsg, setReserveMsg] = useState('');
  const [reserveCap, setReserveCap] = useState(0);    // = sessions purchased
  const [upcomingClasses, setUpcomingClasses] = useState([]);
  // A private lesson was bought: the next step is saying which coach and when.
  // Detected by name, as the plans that sell privates are all called that.
  const boughtPrivate = /\bprivate\b/i.test(decodeURIComponent(searchParams.get('planName') || ''));
  const [privateOpen, setPrivateOpen] = useState(false);

  useEffect(() => {
    const processPayment = async () => {
      const planId = searchParams.get('planId');
      const athleteId = searchParams.get('athleteId');
      if (athleteId) setPaAthleteId(athleteId);
      const sessions = parseInt(searchParams.get('sessions') || '0');
      if (sessions > 0) setReserveCap(sessions);
      const amount = parseFloat(searchParams.get('amount') || '0');
      const planName = searchParams.get('planName') || '';
      const shouldPromptReserve = searchParams.get('promptReserve') === 'true';
      const classType = searchParams.get('reserveClassType') || '';
      const autoCheckinClassId = searchParams.get('autoCheckinClassId') || '';

      if (shouldPromptReserve) {
        setPromptReserve(true);
        setReserveClassType(classType);
        // Load upcoming classes matching type
        try {
          const today = localToday();
          const classSnap = await getDocs(collection(db, 'classes'));
          const upcoming = classSnap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(c =>
              c.date >= today &&
              c.status !== 'canceled' &&
              (!classType || c.sessionType === classType)
            )
            .sort((a, b) => a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime))
            .slice(0, 5);
          setUpcomingClasses(upcoming);
        } catch (e) { /* silently fail */ }
      }

      if (!athleteId || !sessions) {
        setProcessing(false);
        setDone(true);
        return;
      }

      try {
        // Give webhook up to 3 seconds to process first
        await new Promise(r => setTimeout(r, 3000));

        const sessionId = searchParams.get('session_id');

        let athleteDocRef = doc(db, 'athletes', athleteId);
        let athleteDoc = await getDoc(athleteDocRef);

        if (!athleteDoc.exists()) {
          const snap = await getDocs(query(collection(db, 'athletes'), where('athleteId', '==', athleteId)));
          if (!snap.empty) {
            athleteDocRef = snap.docs[0].ref;
            athleteDoc = snap.docs[0];
          } else {
            setProcessing(false);
            setDone(true);
            return;
          }
        }

        if (sessionId) {
          const existingSnap = await getDocs(query(
            collection(db, 'purchases'),
            where('stripeSessionId', '==', sessionId)
          ));
          if (!existingSnap.empty) {
            console.log('Payment already processed by webhook, skipping duplicate.');
            setProcessing(false);
            setDone(true);
            return;
          }
        }

        const currentBalance = athleteDoc.data().sessionBalance || 0;
        const athleteName = `${athleteDoc.data().firstName} ${athleteDoc.data().lastName}`;

        // increment, not currentBalance + sessions: the read above may already
        // be stale by the time this write lands.
        await updateDoc(athleteDocRef, { sessionBalance: increment(sessions) });

        await addDoc(collection(db, 'purchases'), {
          athleteId, planId, athleteName,
          planName: decodeURIComponent(planName),
          amountPaid: amount,
          paymentMethod: 'card',
          paymentStatus: 'paid',
          sessionsAdded: sessions,
          purchaseDate: new Date(),
          createdAt: new Date(),
        });

        // Auto check-in if classId provided (QR flow)
        if (autoCheckinClassId && athleteId) {
          try {
            const classDoc = await getDoc(doc(db, 'classes', autoCheckinClassId));
            if (classDoc.exists()) {
              const cd = classDoc.data();
              // Check not already checked in
              const existingAtt = await getDocs(query(
                collection(db, 'attendance'),
                where('classId', '==', autoCheckinClassId),
                where('athleteId', '==', athleteId)
              ));
              if (existingAtt.empty) {
                await addDoc(collection(db, 'attendance'), {
                  classStartsAt: classStartsAt(cd),
                  classId: autoCheckinClassId,
                  athleteId,
                  athleteName,
                  date: cd.date || '',
                  sessionType: cd.sessionType || '',
                  checkedInAt: new Date(),
                  balanceBefore: currentBalance + sessions,
                  balanceAfter: currentBalance + sessions - 1,
                  autoCheckedIn: true,
                });
              }
            }
          } catch (e) {
            console.error('Auto check-in failed:', e);
          }
        }

      } catch (err) {
        console.error('Error processing payment:', err);
        logError('PaymentSuccess', err.message || String(err), err.stack || '');
      }

      setProcessing(false);
      setDone(true);
    };

    processPayment();
  }, []);

  const handleReserveClass = async (c) => {
    if (!paAthleteId || reservedIds.includes(c.id)) return;
    if (reserveCap && reservedIds.length >= reserveCap) {
      setReserveMsg(`You've reserved all ${reserveCap} of your sessions. Manage the rest anytime from your confirmation email.`);
      return;
    }
    setReservingId(c.id);
    setReserveMsg('');
    try {
      const functions = getFunctions();
      const reserve = httpsCallable(functions, 'reserveAfterPurchase');
      const r = await reserve({ athleteId: paAthleteId, classId: c.id });
      if (r.data?.ok) {
        setReservedIds(prev => [...new Set([...prev, c.id])]);
        const left = reserveCap ? Math.max(0, reserveCap - (reservedIds.length + 1)) : null;
        setReserveMsg(
          r.data.already ? "You're already booked for that class!"
          : left === null ? 'Spot reserved — see you on the sand!'
          : left > 0 ? `Spot reserved! You can reserve ${left} more session${left === 1 ? '' : 's'}.`
          : "Spot reserved! That's all your sessions booked — see you on the sand!"
        );
      } else if (r.data?.full) {
        setReserveMsg('That class is full — try another day.');
      }
    } catch (e) {
      console.error('reserve failed:', e);
      setReserveMsg('Could not reserve. You can also book from the schedule.');
    }
    setReservingId(null);
  };

  return (
    <Layout>
      <Box sx={{
        background: dark, minHeight: '100%', display: 'flex',
        justifyContent: 'center', alignItems: 'center', p: 3,
        fontFamily: '"Barlow", sans-serif',
      }}>
        <Box sx={{
          background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(201,168,76,0.2)',
          borderRadius: '16px', p: { xs: 4, sm: 6 }, textAlign: 'center', maxWidth: 480, width: '100%',
        }}>
          {processing ? (
            <>
              <CircularProgress sx={{ color: gold, mb: 3 }} />
              <Box sx={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.5)', letterSpacing: '0.5px' }}>
                Processing your payment…
              </Box>
            </>
          ) : (
            <>
              <CheckCircleOutlineIcon sx={{ fontSize: 72, color: gold, mb: 2 }} />
              <Box sx={{
                fontFamily: '"Bebas Neue", sans-serif', fontSize: 'clamp(1.8rem,4vw,2.4rem)',
                letterSpacing: '3px', color: 'white', lineHeight: 1, mb: 1,
              }}>
                Payment Successful
              </Box>
              <Box sx={{ width: 50, height: 2, background: `linear-gradient(90deg, transparent, ${gold}, transparent)`, mx: 'auto', my: 2 }} />
              <Box sx={{ fontSize: '0.88rem', fontWeight: 300, color: 'rgba(255,255,255,0.45)', mb: 4, lineHeight: 1.6 }}>
                Your sessions have been added to your account. You're all set!
              </Box>

              {/* Private lesson: ask for a coach and a time. */}
              {boughtPrivate && paAthleteId && (
                <Box sx={{ mb: 4, p: 3, border: '1px solid rgba(201,168,76,0.25)', borderRadius: '12px', background: 'rgba(201,168,76,0.06)', textAlign: 'left' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CalendarMonthIcon sx={{ color: gold, fontSize: 20 }} />
                    <Box sx={{ fontSize: '0.95rem', fontWeight: 700, color: 'white', letterSpacing: '0.5px' }}>
                      Book your private lesson
                    </Box>
                  </Box>
                  <Box sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', mb: 2, lineHeight: 1.5 }}>
                    Pick your preferred coach and tell us when you're free. We'll confirm the time.
                  </Box>
                  <Box component="button" onClick={() => setPrivateOpen(true)} sx={{
                    fontFamily: '"Barlow", sans-serif', fontSize: '0.8rem', fontWeight: 600, letterSpacing: '1px',
                    textTransform: 'uppercase', color: '#0D1E35', background: gold, border: 'none',
                    borderRadius: '6px', px: 2.5, py: 1.2, cursor: 'pointer', '&:hover': { background: '#e0be6a' },
                  }}>
                    Choose a coach and time
                  </Box>
                </Box>
              )}
              <PrivateSessionRequestDialog open={privateOpen} onClose={() => setPrivateOpen(false)} athleteId={paAthleteId} />

              {/* Reserve a class prompt */}
              {promptReserve && (
                <Box sx={{ mb: 4, p: 3, border: '1px solid rgba(201,168,76,0.25)', borderRadius: '12px', background: 'rgba(201,168,76,0.06)', textAlign: 'left' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CalendarMonthIcon sx={{ color: gold, fontSize: 20 }} />
                    <Box sx={{ fontSize: '0.95rem', fontWeight: 700, color: 'white', letterSpacing: '0.5px' }}>
                      Reserve Your Spot
                    </Box>
                  </Box>
                  <Box sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', mb: 2, lineHeight: 1.5 }}>
                    Book a spot in an upcoming class to get started!
                  </Box>
                  {upcomingClasses.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                      {upcomingClasses.map(c => {
                        const fmtTime = t => {
                          if (!t) return '';
                          const [h, min] = t.split(':').map(Number);
                          return `${h % 12 || 12}:${String(min).padStart(2,'0')} ${h >= 12 ? 'PM' : 'AM'}`;
                        };
                        const dateLabel = new Date(c.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                        const isReserved = reservedIds.includes(c.id);
                        const isBusy = reservingId === c.id;
                        const capReached = reserveCap > 0 && reservedIds.length >= reserveCap;
                        const clickable = !!paAthleteId && !isReserved && !capReached;
                        return (
                          <Box key={c.id}
                            onClick={() => clickable && !isBusy && handleReserveClass(c)}
                            sx={{
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderRadius: '8px',
                              background: isReserved ? 'rgba(201,168,76,0.18)' : 'rgba(255,255,255,0.05)',
                              border: isReserved ? `1px solid ${gold}` : '1px solid rgba(255,255,255,0.08)',
                              cursor: clickable ? 'pointer' : 'default',
                              transition: 'all 0.15s',
                              ...(clickable && !isReserved ? { '&:hover': { background: 'rgba(201,168,76,0.10)', borderColor: 'rgba(201,168,76,0.5)' } } : {}),
                            }}>
                            <Box>
                              <Box sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'white' }}>{dateLabel}</Box>
                              <Box sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)' }}>
                                {fmtTime(c.startTime)}–{fmtTime(c.endTime)}{c.courtNumber ? ` · Court ${c.courtNumber.replace('court-', '')}` : ''}
                                {c.skillGroup ? ` · ${c.skillGroup}` : ''}
                              </Box>
                            </Box>
                            <Box sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: isReserved ? gold : 'rgba(201,168,76,0.55)' }}>
                              {isBusy ? <CircularProgress size={14} sx={{ color: gold }} /> : isReserved ? 'Reserved ✓' : (clickable ? 'Reserve' : '')}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : null}
                  {reserveMsg && (
                    <Box sx={{ fontSize: '0.78rem', color: gold, mb: 2 }}>{reserveMsg}</Box>
                  )}
                  <Box
                    onClick={() => navigate('/schedule')}
                    sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 1,
                      background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.4)',
                      color: gold, borderRadius: '8px', px: 3, py: 1.2, cursor: 'pointer',
                      fontSize: '0.82rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase',
                      transition: 'all 0.2s', '&:hover': { background: 'rgba(201,168,76,0.25)', borderColor: gold },
                    }}
                  >
                    <CalendarMonthIcon sx={{ fontSize: 16 }} />
                    View Full Schedule
                  </Box>
                </Box>
              )}
              <Box
                onClick={() => navigate('/training-plans')}
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 1,
                  background: gold, color: dark, borderRadius: '8px',
                  px: 4, py: 1.5, cursor: 'pointer',
                  fontFamily: '"Barlow", sans-serif', fontSize: '0.88rem', fontWeight: 700,
                  letterSpacing: '1px', textTransform: 'uppercase',
                  transition: 'background 0.2s', mr: 2,
                  '&:hover': { background: '#e0be6a' },
                }}
              >
                <ShoppingCartIcon sx={{ fontSize: 16 }} />
                Buy More
              </Box>
              <Box
                onClick={() => navigate('/dashboard')}
                sx={{
                  display: 'inline-flex', alignItems: 'center',
                  border: '1px solid rgba(201,168,76,0.3)', color: 'rgba(201,168,76,0.7)',
                  borderRadius: '8px', px: 4, py: 1.5, cursor: 'pointer',
                  fontFamily: '"Barlow", sans-serif', fontSize: '0.88rem', fontWeight: 600,
                  letterSpacing: '1px', textTransform: 'uppercase',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: gold, color: gold },
                }}
              >
                Dashboard
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Layout>
  );
}

export default PaymentSuccess;
